import { Alert, App, Collapse, Dropdown, Button } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import type { FieldMeta } from '../../api/types';
import type { ContractSection, ContractSectionType } from '../../contract/types';
import { INSERTABLE_SECTION_TYPES, SECTION_TYPE_LABELS, genId } from '../../contract/types';
import { migrateDetailTable } from '../../../render-core/migrateTable';
import { createFreeTable } from '../../contract/freeTable';
import { defaultParty } from '../../contract/signature';
import { HeaderEditor } from './editors/HeaderEditor';
import { InfoGridEditor } from './editors/InfoGridEditor';
import { TextBlockEditor } from './editors/TextBlockEditor';
import { DetailTableEditor } from './editors/DetailTableEditor';
import { FreeTableEditor } from './editors/FreeTableEditor';
import { SignatureEditor } from './editors/SignatureEditor';
import { AttachmentEditor } from './editors/AttachmentEditor';
import { CodeEditor } from './editors/CodeEditor';
import { RichTextEditor } from './editors/RichTextEditor';
import { DividerEditor } from './editors/DividerEditor';
import { ColumnsEditor } from './editors/ColumnsEditor';
import { findSectionDeep, isColumns } from '../../../render-core/sectionTree';
import { VisibilityRuleEditor } from './editors/VisibilityRuleEditor';
import type { VisibilityRule } from '../../contract/types';

interface Props {
  sections: ContractSection[];
  onChange: (sections: ContractSection[]) => void;
  fieldMetas: FieldMeta[];
  /** 受控展开：与画布点选联动（不传则维持原非受控行为）。 */
  activeId?: string | null;
  onActiveChange?: (id: string | null) => void;
  /** 条件显隐规则表（sectionId → 规则）。 */
  visibility?: Record<string, VisibilityRule>;
  onVisibilityChange?: (visibility: Record<string, VisibilityRule>) => void;
  /** 分栏里的嵌套列表：空态和加区块按钮都收小。 */
  compact?: boolean;
}

export function createDefaultSection(type: ContractSectionType): ContractSection {
  const id = genId();
  switch (type) {
    case 'header':
      return { type: 'header', id, content: '', align: 'center', fontSizePx: 18, bold: true };
    case 'info_grid':
      return { type: 'info_grid', id, columns: 2, items: [] };
    case 'detail_table':
      return {
        type: 'detail_table',
        id,
        linkFieldId: '',
        linkedTableId: '',
        columns: [],
        showIndex: true,
        summaryRow: null,
      };
    case 'free_table':
      return { type: 'free_table', id, borderPx: 1, fontSizePx: null, ...createFreeTable(3, 4) };
    case 'text_block':
      return { type: 'text_block', id, content: '', fontSizePx: null };
    case 'signature':
      return {
        type: 'signature',
        id,
        parties: ['甲方（盖章）', '乙方（盖章）'].map((label) => defaultParty(label)),
      };
    case 'attachment':
      return {
        type: 'attachment',
        id,
        fieldId: null,
        title: '',
        layout: 'full',
        maxHeightMm: null,
        showName: true,
        pageBreakEach: false,
      };
    case 'code':
      return {
        type: 'code',
        id,
        codeType: 'qr',
        fieldId: null,
        staticValue: '',
        sizePx: 120,
        align: 'left',
        showText: true,
      };
    case 'rich_text':
      return { type: 'rich_text', id, fieldId: null, format: 'markdown' };
    case 'divider':
      return { type: 'divider', id, style: 'line' };
    case 'columns':
      return { type: 'columns', id, widths: [50, 50], columns: [[], []], gapMm: 4 };
  }
}

export function SectionEditor({
  sections,
  onChange,
  fieldMetas,
  activeId,
  onActiveChange,
  visibility,
  onVisibilityChange,
  compact,
}: Props) {
  const { modal } = App.useApp();
  const controlled = activeId !== undefined;

  const setRule = (secId: string, rule: VisibilityRule | undefined) => {
    if (!onVisibilityChange) return;
    const next = { ...(visibility || {}) };
    if (rule) next[secId] = rule;
    else delete next[secId];
    onVisibilityChange(next);
  };
  const updateSection = (id: string, updated: ContractSection) => {
    onChange(sections.map((s) => (s.id === id ? updated : s)));
  };

  const removeSection = (id: string) => {
    onChange(sections.filter((s) => s.id !== id));
  };

  const moveSection = (id: string, dir: -1 | 1) => {
    const idx = sections.findIndex((s) => s.id === id);
    if (idx === -1) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const next = [...sections];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next);
  };

  /** 旧明细表格 → 新表格。区块 id 不变，条件显隐等引用不会断。 */
  const upgradeDetailTable = (sec: ContractSection) => {
    if (sec.type !== 'detail_table') return;
    const { section: next, lost } = migrateDetailTable(sec);
    onChange(sections.map((s) => (s.id === sec.id ? next : s)));
    modal.success({
      title: '已升级为新表格',
      content:
        lost.length > 0
          ? `以下设置新表格没有对应能力，需要你重新处理：${lost.join('、')}。`
          : '列、字段绑定和合计行都已带过来，可以直接在表格里合并单元格了。',
    });
  };

  const addSection = (type: ContractSectionType) => {
    onChange([...sections, createDefaultSection(type)]);
  };

  const addMenuItems = INSERTABLE_SECTION_TYPES.map(
    (type) => ({
      key: type,
      label: SECTION_TYPE_LABELS[type],
    }),
  );

  if (sections.length === 0 && compact) {
    return (
      <div style={{ textAlign: 'left', padding: '4px 0' }}>
        <Dropdown
          menu={{ items: addMenuItems, onClick: ({ key }) => addSection(key as ContractSectionType) }}
          trigger={['click']}
        >
          <Button icon={<PlusOutlined />} size="small" type="dashed">
            这一栏还是空的，添加区块
          </Button>
        </Dropdown>
      </div>
    );
  }
  if (sections.length === 0) {
    // antd 的 Empty 默认插画是灰调的，跟这套暖色纸感主题很割裂。
    // 换成一张虚线"待填的纸"，用主题 token，视觉上也更贴"排版打印"这件事。
    return (
      <div>
        <div
          style={{
            border: '1px dashed var(--border-strong)',
            borderRadius: 10,
            background: 'var(--surface-2)',
            padding: '22px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>还是一张白纸</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.6 }}>
            加入标题、字段、表格等区块，上方预览区会实时出现效果
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <Dropdown
            menu={{ items: addMenuItems, onClick: ({ key }) => addSection(key as ContractSectionType) }}
            trigger={['click']}
          >
            <Button icon={<PlusOutlined />} type="primary" size="small">
              添加区块
            </Button>
          </Dropdown>
        </div>
      </div>
    );
  }

  const items = sections.map((sec, idx) => ({
    key: sec.id,
    label: (
      <div
        id={`pp-panel-${sec.id}`}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
      >
        <span>{SECTION_TYPE_LABELS[sec.type]}</span>
        <span onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            type="text"
            icon={<ArrowUpOutlined />}
            disabled={idx === 0}
            onClick={() => moveSection(sec.id, -1)}
          />
          <Button
            size="small"
            type="text"
            icon={<ArrowDownOutlined />}
            disabled={idx === sections.length - 1}
            onClick={() => moveSection(sec.id, 1)}
          />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeSection(sec.id)}
          />
        </span>
      </div>
    ),
    children: (
      <>
        {renderEditor(sec)}
        {onVisibilityChange && (
          <VisibilityRuleEditor
            rule={visibility?.[sec.id]}
            fieldMetas={fieldMetas}
            onChange={(rule) => setRule(sec.id, rule)}
          />
        )}
      </>
    ),
  }));

  function renderEditor(sec: ContractSection) {
    switch (sec.type) {
      case 'header':
        return <HeaderEditor section={sec} onChange={(s) => updateSection(sec.id, s)} fieldMetas={fieldMetas} />;
      case 'info_grid':
        return <InfoGridEditor section={sec} onChange={(s) => updateSection(sec.id, s)} fieldMetas={fieldMetas} />;
      case 'detail_table':
        return (
          <>
            {/* 旧版明细表格：能带子表多行但不能合并单元格。新版「表格」是它的
                超集，给一键升级，用户不用重新搭一遍。丢掉的能力如实列出来。 */}
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message="这是旧版明细表格"
              description={
                <div style={{ fontSize: 11.5, lineHeight: 1.7 }}>
                  新版「表格」支持合并单元格、斑马纹、「共 N 条」，数据来源也能切换。
                  <div style={{ marginTop: 6 }}>
                    <Button size="small" type="primary" onClick={() => upgradeDetailTable(sec)}>
                      升级为新表格
                    </Button>
                  </div>
                </div>
              }
            />
            <DetailTableEditor section={sec} onChange={(s) => updateSection(sec.id, s)} fieldMetas={fieldMetas} />
          </>
        );
      case 'free_table':
        return <FreeTableEditor section={sec} onChange={(s) => updateSection(sec.id, s)} fieldMetas={fieldMetas} />;
      case 'text_block':
        return <TextBlockEditor section={sec} onChange={(s) => updateSection(sec.id, s)} fieldMetas={fieldMetas} />;
      case 'signature':
        return <SignatureEditor section={sec} onChange={(s) => updateSection(sec.id, s)} fieldMetas={fieldMetas} />;
      case 'attachment':
        return <AttachmentEditor section={sec} onChange={(s) => updateSection(sec.id, s)} fieldMetas={fieldMetas} />;
      case 'code':
        return <CodeEditor section={sec} onChange={(s) => updateSection(sec.id, s)} fieldMetas={fieldMetas} />;
      case 'rich_text':
        return <RichTextEditor section={sec} onChange={(s) => updateSection(sec.id, s)} fieldMetas={fieldMetas} />;
      case 'divider':
        return <DividerEditor section={sec} onChange={(s) => updateSection(sec.id, s)} />;
      case 'columns':
        return (
          <>
            <ColumnsEditor section={sec} onChange={(s) => updateSection(sec.id, s)} />
            {sec.columns.map((col, i) => (
              <div
                key={i}
                style={{
                  marginTop: 10,
                  paddingLeft: 8,
                  borderLeft: '2px solid var(--border-strong)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', marginBottom: 6 }}>
                  第 {i + 1} 栏
                </div>
                <SectionEditor
                  compact
                  sections={col}
                  onChange={(next) =>
                    updateSection(sec.id, { ...sec, columns: sec.columns.map((c, k) => (k === i ? next : c)) })
                  }
                  fieldMetas={fieldMetas}
                  activeId={activeId}
                  onActiveChange={onActiveChange}
                  visibility={visibility}
                  onVisibilityChange={onVisibilityChange}
                />
              </div>
            ))}
          </>
        );
    }
  }

  // 画布上点中了某一栏里的区块：外层要把它所在的分栏面板也展开，否则看不见
  const activeKeys = activeId
    ? sections
        .filter((s) => s.id === activeId || (isColumns(s) && !!findSectionDeep(s.columns.flat(), activeId)))
        .map((s) => s.id)
    : [];

  return (
    <div>
      <Collapse
        size="small"
        items={items}
        {...(controlled
          ? {
              activeKey: activeKeys,
              onChange: (keys: string | string[]) => {
                const arr = Array.isArray(keys) ? keys : [keys];
                // 受控单开：取最新点击的面板
                const next = arr.filter((k) => k !== activeId);
                onActiveChange?.(next[next.length - 1] ?? null);
              },
            }
          : { defaultActiveKey: sections.length > 0 ? [sections[0].id] : [] })}
      />
      <div style={{ textAlign: 'center', marginTop: 8 }}>
        <Dropdown
          menu={{ items: addMenuItems, onClick: ({ key }) => addSection(key as ContractSectionType) }}
          trigger={['click']}
        >
          <Button icon={<PlusOutlined />} size="small">
            添加区块
          </Button>
        </Dropdown>
      </div>
    </div>
  );
}
